import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

interface DropdownProps {
  children: React.ReactNode;
  title: string;
}

const Dropdown = ({ children, title }: DropdownProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleClickOutside = (e: MouseEvent): void => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node) &&
      toggleButtonRef.current !== e.target
    ) {
      setIsOpen(false);
    }
  };

  const handleDropdownItemClick = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full">
      <button
        ref={toggleButtonRef}
        onClick={toggleDropdown}
        className="px-4 py-2 rounded-md border border-navy-200 w-full flex justify-between items-center transition"
        data-testid="dropdown-toggle"
      >
        <span className="text-navy-500 font-semibold">{title}</span>
        <Icons.Arrow
          className={classNames('rotate-90 transition shrink-0 text-navy-500', {
            '!-rotate-90': isOpen,
          })}
        />
      </button>
      {isOpen && (
        <div
          ref={dropdownRef}
          role="menu"
          tabIndex={-1}
          onClick={handleDropdownItemClick}
          onKeyDown={(e) => e.key === 'Enter' && handleDropdownItemClick()}
          className="absolute left-0 py-2 bg-white border rounded-md shadow !w-full z-20 max-h-[200px] overflow-y-scroll custom-scroll"
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
